import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { getContextToken } from './shopwareApi';
import { useShopware } from '~/components-shop/shopware/ShopwareContext';
import { getCookie } from '../utils/cookieStorage';

// Non-httpOnly companion of the encrypted SSO tokens cookie. Planted by
// Pimcore whenever a login succeeds. Absence means the user has no Pimcore
// session — there is nothing to hand off to Shopware, so we skip silently.
const SSO_TIMESTAMP_COOKIE = 'orthomol_sso_timestamp';

interface UseEnsureShopwareLoginOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseEnsureShopwareLoginResult {
  isLoggedIn: boolean;
  loginError: boolean;
  retry: () => void;
}

/**
 * Custom hook to ensure user is logged in to Shopware.
 *
 * Runs once on mount. On a successful login, is pinned to a done state and
 * won't retry. On failure, stays in a retryable state — callers can invoke
 * the returned `retry()` (or re-mount) to try again.
 *
 * Logic:
 * - If context token exists on mount: validates by fetching customer
 *   - 200: valid customer token
 *   - 403: guest token → upgrade via SSO login
 * - If no context token on mount: performs SSO login
 */
export const useEnsureShopwareLogin = ({
  onSuccess,
  onError,
}: UseEnsureShopwareLoginOptions): UseEnsureShopwareLoginResult => {
  const { login, fetchCustomer } = useShopware();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);

  // Pinned to true only AFTER a successful login. On failure we stay false
  // so a retry can re-attempt — a single transient network error at first
  // mount must not lock the user out permanently.
  const hasRunRef = useRef<boolean>(false);
  // Prevents parallel in-flight attempts when the effect re-runs or retry()
  // is called before the previous attempt resolved.
  const inFlightRef = useRef<boolean>(false);
  // Tracks whether the hook's host component is still mounted so async
  // work that resolves after unmount doesn't set state on a gone component.
  const isMountedRef = useRef<boolean>(true);

  const runEnsureLogin = useCallback(async () => {
    if (hasRunRef.current || inFlightRef.current) {
      return;
    }
    if (getCookie(SSO_TIMESTAMP_COOKIE) === null) {
      return;
    }
    inFlightRef.current = true;

    const markSuccess = () => {
      hasRunRef.current = true;
      if (!isMountedRef.current) {
        return;
      }
      setIsLoggedIn(true);
      setLoginError(false);
      if (onSuccess) {
        onSuccess();
      }
    };

    try {
      const existingToken = getContextToken();

      if (existingToken) {
        try {
          await fetchCustomer();
          console.log('[useEnsureShopwareLogin] Customer token is valid');
          markSuccess();
          return;
        } catch (error) {
          if (error instanceof Error && error.message.includes('403')) {
            console.log(
              '[useEnsureShopwareLogin] Guest token detected (403), attempting to upgrade to customer token...'
            );
            await login();
            markSuccess();
            return;
          }
          throw error;
        }
      }

      console.log('[useEnsureShopwareLogin] No context token found, logging in via SSO...');
      await login();
      markSuccess();
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      console.error('[useEnsureShopwareLogin] Failed to ensure login:', error);
      setLoginError(true);
      setIsLoggedIn(false);

      if (onError && error instanceof Error) {
        onError(error);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [login, fetchCustomer, onSuccess, onError]);

  useEffect(() => {
    isMountedRef.current = true;
    void runEnsureLogin();
    return () => {
      // Host unmounting (tab close, SPA navigation) — stop feeding state
      // updates into a dead component. The in-flight request itself is
      // allowed to finish; we just ignore its resolution.
      isMountedRef.current = false;
    };
  }, [runEnsureLogin]);

  // Re-validate when the page is restored from the bfcache. A bfcache restore
  // does not remount the component, so the mount effect above never re-runs —
  // a session established (or expired) while the page was frozen would be
  // missed. Mirrors ShopwareContext's pageshow handling.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || inFlightRef.current) {
        return;
      }
      if (getCookie(SSO_TIMESTAMP_COOKIE) === null) {
        return;
      }
      // Reset the done-pin so validation runs again for this restore.
      hasRunRef.current = false;
      setLoginError(false);
      void runEnsureLogin();
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [runEnsureLogin]);

  const retry = useCallback(() => {
    // If a previous attempt is still in flight, don't clear the error state —
    // the in-flight attempt will finish and flip isLoggedIn / loginError
    // itself. Clearing prematurely would hide the banner and leave the UI
    // in an ambiguous "looks fine but nothing is happening" state.
    if (inFlightRef.current) {
      return;
    }
    hasRunRef.current = false;
    setLoginError(false);
    void runEnsureLogin();
  }, [runEnsureLogin]);

  return { isLoggedIn, loginError, retry };
};
